import type { components } from "#/schema";
import { Beweegthema, Leerjaar, Leerlijn, SociaalEmotioneelDomein } from "#/schema.nswag";
import { isApiEnumValue } from "#/modules/util.helpers/zod-enum";

const isLeerjaar = isApiEnumValue(Leerjaar);
const isLeerlijn = isApiEnumValue(Leerlijn);
const isBeweegthema = isApiEnumValue(Beweegthema);
const isSociaalEmotioneelDomein = isApiEnumValue(SociaalEmotioneelDomein);

// `m` (paraglide messages) is auto-imported (see auto-imports.d.ts / unplugin-auto-import).

function getInnerLeerjaarTranslation(key: components["schemas"]["Leerjaar"]) {
  switch (key) {
    case "None":
      return m["enums.Leerjaar.None"]();
    case "Een":
      return m["enums.Leerjaar.Een"]();
    case "Twee":
      return m["enums.Leerjaar.Twee"]();
    case "Drie":
      return m["enums.Leerjaar.Drie"]();
    case "Vier":
      return m["enums.Leerjaar.Vier"]();
    case "Vijf":
      return m["enums.Leerjaar.Vijf"]();
    case "Zes":
      return m["enums.Leerjaar.Zes"]();
    case "Zeven":
      return m["enums.Leerjaar.Zeven"]();
    case "Acht":
      return m["enums.Leerjaar.Acht"]();
    default: {
      const exhaustiveCheck: never = key;
      return exhaustiveCheck;
    }
  }
}

export function getLeerjaarTranslation(key: components["schemas"]["Leerjaar"]) {
  // if the key contains a , we can assume its a combo, so we split it and get the translation for each
  if (key.includes(",")) {
    const parts = key
      .split(",")
      .map((part) => part.trim())
      .filter(isLeerjaar);
    return `${m["common.leerjaar"]()} ${parts
      .map((part) => getInnerLeerjaarNumberTranslation(part))
      .join("/")}`;
  }

  return getInnerLeerjaarTranslation(key);
}

export function getInnerLeerjaarNumberTranslation(key: components["schemas"]["Leerjaar"]) {
  switch (key) {
    case "None":
      return m["enums.LeerjaarNumber.None"]();
    case "Een":
      return m["enums.LeerjaarNumber.Een"]();
    case "Twee":
      return m["enums.LeerjaarNumber.Twee"]();
    case "Drie":
      return m["enums.LeerjaarNumber.Drie"]();
    case "Vier":
      return m["enums.LeerjaarNumber.Vier"]();
    case "Vijf":
      return m["enums.LeerjaarNumber.Vijf"]();
    case "Zes":
      return m["enums.LeerjaarNumber.Zes"]();
    case "Zeven":
      return m["enums.LeerjaarNumber.Zeven"]();
    case "Acht":
      return m["enums.LeerjaarNumber.Acht"]();
    default: {
      const exhaustiveCheck: never = key;
      return exhaustiveCheck;
    }
  }
}

export function getLeerjaarComboTranslation(key: components["schemas"]["LeerjaarCombo"]) {
  switch (key) {
    case "None":
      return m["enums.LeerjaarCombo.None"]();
    case "EenTwee":
      return m["enums.LeerjaarCombo.EenTwee"]();
    case "DrieVier":
      return m["enums.LeerjaarCombo.DrieVier"]();
    case "VijfZes":
      return m["enums.LeerjaarCombo.VijfZes"]();
    case "ZevenAcht":
      return m["enums.LeerjaarCombo.ZevenAcht"]();
    default: {
      const exhaustiveCheck: never = key;
      return exhaustiveCheck;
    }
  }
}

export function getLeerlijnTranslation(key: components["schemas"]["Leerlijn"]) {
  switch (key) {
    case "Balanceren":
      return m["enums.Leerlijn.Balanceren"]();
    case "Klimmen":
      return m["enums.Leerlijn.Klimmen"]();
    case "Zwaaien":
      return m["enums.Leerlijn.Zwaaien"]();
    case "OverDeKopGaan":
      return m["enums.Leerlijn.OverDeKopGaan"]();
    case "Springen":
      return m["enums.Leerlijn.Springen"]();
    case "Hardlopen":
      return m["enums.Leerlijn.Hardlopen"]();
    case "Mikken":
      return m["enums.Leerlijn.Mikken"]();
    case "Jongleren":
      return m["enums.Leerlijn.Jongleren"]();
    case "Doelspelen":
      return m["enums.Leerlijn.Doelspelen"]();
    case "Tikspelen":
      return m["enums.Leerlijn.Tikspelen"]();
    case "Stoeispelen":
      return m["enums.Leerlijn.Stoeispelen"]();
    case "BewegenOpMuziek":
      return m["enums.Leerlijn.BewegenOpMuziek"]();
    default: {
      const exhaustiveCheck: never = key;
      return exhaustiveCheck;
    }
  }
}

export function getNotitieTypeTranslation(key: components["schemas"]["NotitieType"]) {
  switch (key) {
    case "Beweegresultaat":
      return m["enums.NotitieType.Beweegresultaat"]();
    case "SociaalEmotioneelThemaResultaat":
      return m["enums.NotitieType.SociaalEmotioneelThemaResultaat"]();
    case "Administratie":
      return m["enums.NotitieType.Administratie"]();
    case "Rapport":
      return m["enums.NotitieType.Rapport"]();
    case "Les":
      return m["enums.NotitieType.Les"]();
    case "Leerling":
      return m["enums.NotitieType.Leerling"]();
    default: {
      const exhaustiveCheck: never = key;
      return exhaustiveCheck;
    }
  }
}

export function getLicentieTypeTranslation(key: components["schemas"]["LicentieType"]) {
  switch (key) {
    case "Proefperiode":
      return m["enums.LicentieType.Proefperiode"]();
    case "Abonnement":
      return m["enums.LicentieType.Abonnement"]();
    case "Showcase":
      return m["enums.LicentieType.Showcase"]();
    default: {
      const exhaustiveCheck: never = key;
      return exhaustiveCheck;
    }
  }
}

export function getGroepeerTypeTranslation(key: components["schemas"]["GroepeerType"]) {
  switch (key) {
    case "Homogeen":
      return m["enums.GroepeerType.Homogeen"]();
    case "Heterogeen":
      return m["enums.GroepeerType.Heterogeen"]();
    default: {
      const exhaustiveCheck: never = key;
      return exhaustiveCheck;
    }
  }
}

export function getGenderTranslation(key: components["schemas"]["Gender"]) {
  switch (key) {
    case "Man":
      return m["enums.Gender.Man"]();
    case "Vrouw":
      return m["enums.Gender.Vrouw"]();
    default: {
      const exhaustiveCheck: never = key;
      return exhaustiveCheck;
    }
  }
}

export function getFilterProgressStatusTranslation(
  key: components["schemas"]["FilterProgressStatus"],
) {
  switch (key) {
    case "None":
      return m["enums.FilterProgressStatus.None"]();
    case "InProgress":
      return m["enums.FilterProgressStatus.InProgress"]();
    case "Completed":
      return m["enums.FilterProgressStatus.Completed"]();
    default: {
      const exhaustiveCheck: never = key;
      return exhaustiveCheck;
    }
  }
}

export function getLesStatusTranslation(key: components["schemas"]["LesStatus"]) {
  switch (key) {
    case "Concept":
      return m["enums.LesStatus.Concept"]();
    case "Actief":
      return m["enums.LesStatus.Actief"]();
    case "Afgerond":
      return m["enums.LesStatus.Afgerond"]();
    default: {
      const exhaustiveCheck: never = key;
      return exhaustiveCheck;
    }
  }
}

export function getSociaalEmotioneelDomeinTranslation(
  key: components["schemas"]["SociaalEmotioneelDomein"],
) {
  switch (key) {
    case "Zelfbewustzijn":
      return m["enums.SociaalEmotioneelDomein.Zelfbewustzijn"]();
    case "Zelfsturing":
      return m["enums.SociaalEmotioneelDomein.Zelfsturing"]();
    case "Inlevingsvermogen":
      return m["enums.SociaalEmotioneelDomein.Inlevingsvermogen"]();
    case "Samenwerken":
      return m["enums.SociaalEmotioneelDomein.Samenwerken"]();
    case "MoreelKompas":
      return m["enums.SociaalEmotioneelDomein.MoreelKompas"]();
    case "InzetEnGedrag":
      return m["enums.SociaalEmotioneelDomein.InzetEnGedrag"]();
    default: {
      const exhaustiveCheck: never = key;
      return exhaustiveCheck;
    }
  }
}

export function getBeweegThemaTranslation(key: components["schemas"]["Beweegthema"]) {
  switch (key) {
    case "Balanceren":
      return m["enums.Beweegthema.Balanceren"]();
    case "Rijden":
      return m["enums.Beweegthema.Rijden"]();
    case "Glijden":
      return m["enums.Beweegthema.Glijden"]();
    case "Acrobatiek":
      return m["enums.Beweegthema.Acrobatiek"]();
    case "Klauteren":
      return m["enums.Beweegthema.Klauteren"]();
    case "Touwklimmen":
      return m["enums.Beweegthema.Touwklimmen"]();
    case "Schommelen":
      return m["enums.Beweegthema.Schommelen"]();
    case "HangendZwaaien":
      return m["enums.Beweegthema.HangendZwaaien"]();
    case "SteunendZwaaien":
      return m["enums.Beweegthema.SteunendZwaaien"]();
    case "OverDeKopGaan":
      return m["enums.Beweegthema.OverDeKopGaan"]();
    case "VrijeSprongen":
      return m["enums.Beweegthema.VrijeSprongen"]();
    case "Steunspringen":
      return m["enums.Beweegthema.Steunspringen"]();
    case "HoogEnVerspringen":
      return m["enums.Beweegthema.HoogEnVerspringen"]();
    case "TouwtjeSpringen":
      return m["enums.Beweegthema.TouwtjeSpringen"]();
    case "Hardlopen":
      return m["enums.Beweegthema.Hardlopen"]();
    case "HardlopenCoopertest":
      return m["enums.Beweegthema.HardlopenCoopertest"]();
    case "Wegspelen":
      return m["enums.Beweegthema.Wegspelen"]();
    case "Mikken":
      return m["enums.Beweegthema.Mikken"]();
    case "WerpenEnVangen":
      return m["enums.Beweegthema.WerpenEnVangen"]();
    case "Soleren":
      return m["enums.Beweegthema.Soleren"]();
    case "Retourneren":
      return m["enums.Beweegthema.Retourneren"]();
    case "Doelspelen":
      return m["enums.Beweegthema.Doelspelen"]();
    case "Lummelspelen":
      return m["enums.Beweegthema.Lummelspelen"]();
    case "AangepasteSportspelen":
      return m["enums.Beweegthema.AangepasteSportspelen"]();
    case "Keeperspelen":
      return m["enums.Beweegthema.Keeperspelen"]();
    case "Tikspelen":
      return m["enums.Beweegthema.Tikspelen"]();
    case "Afgooispelen":
      return m["enums.Beweegthema.Afgooispelen"]();
    case "Honkloopspelen":
      return m["enums.Beweegthema.Honkloopspelen"]();
    case "Stoeispelen":
      return m["enums.Beweegthema.Stoeispelen"]();
    case "EenDansUitvoeren":
      return m["enums.Beweegthema.EenDansUitvoeren"]();
    default: {
      const exhaustiveCheck: never = key;
      return exhaustiveCheck;
    }
  }
}

/**
 * Resolve a combined leerlijn / beweegthema / sociaal-emotioneel-domein name to its translation,
 * trying each mapping in turn and falling back to the raw name. Used by the klassen-insights
 * sidebar tree filter, whose items mix all three enum kinds.
 */
export function getCombinedThemaDomeinLeerlijnTranslation(name: string): string {
  if (isLeerlijn(name)) return getLeerlijnTranslation(name);
  if (isBeweegthema(name)) return getBeweegThemaTranslation(name);
  if (isSociaalEmotioneelDomein(name)) return getSociaalEmotioneelDomeinTranslation(name);

  // If nothing matches, return the name as-is
  return name;
}

/** "Leerlijn · Beweegthema", or just one name when both are called the same. */
export function getLeerlijnBeweegthemaLabel(
  leerlijn: components["schemas"]["Leerlijn"],
  beweegthema: components["schemas"]["Beweegthema"],
): string {
  const leerlijnLabel = getLeerlijnTranslation(leerlijn);
  const beweegthemaLabel = getBeweegThemaTranslation(beweegthema);
  return leerlijnLabel === beweegthemaLabel
    ? leerlijnLabel
    : `${leerlijnLabel} · ${beweegthemaLabel}`;
}

export function getLeerjaarComboHex(leerjaarCombo: components["schemas"]["LeerjaarCombo"]): string {
  switch (leerjaarCombo) {
    case "EenTwee":
      return "#3B82F6"; // blauw
    case "DrieVier":
      return "#10B981"; // groen
    case "VijfZes":
      return "#F59E0B"; // geel
    case "ZevenAcht":
      return "#EF4444"; // red
    case "None":
    default:
      return "#6b7280"; // gray-500
  }
}

export function getSociaalEmotioneelZinTypeTranslation(
  key: components["schemas"]["SociaalEmotioneelZinType"],
) {
  switch (key) {
    case "IkKan":
      return m["enums.SociaalEmotioneelDoelFormulering.IkKan"]();
    case "IkVind":
      return m["enums.SociaalEmotioneelDoelFormulering.IkVind"]();
    default: {
      const exhaustiveCheck: never = key;
      return exhaustiveCheck;
    }
  }
}

export function getRapportElementenTranslation(key: components["schemas"]["RapportElementen"]) {
  switch (key) {
    case "None":
      return "";
    case "IkCirkelBeweegactiviteiten":
      return m["rapporten.config.elements.ik_cirkel_motorisch"]();
    case "IkCirkelSociaalEmotioneelThemas":
      return m["rapporten.config.elements.ik_cirkel_sociaal_emotioneel"]();
    case "SportdeelnameVoorkant":
      return `${m["rapporten.config.elements.sportdeelname"]()}`;
    case "SportdeelnameAchterkant":
      return `${m["rapporten.config.elements.sportdeelname"]()}`;
    case "RapportNotitie":
      return m["rapporten.config.elements.rapport_notitie"]();
    case "Beweegresultaten":
      return m["rapporten.config.elements.motorische_resultaten"]();
    case "SociaalEmotioneelThemaResultaten":
      return m["rapporten.config.elements.sociaal_emotionele_resultaten"]();
    case "TopResultaten":
      return m["rapporten.config.elements.top_resultaten"]();
    case "Avatar":
      return m["rapporten.config.elements.show_avatar"]();
    case "Handout":
      return m["rapporten.config.elements.handout"]();
    case "ZwemvaardigheidVoorkant":
      return m["rapporten.config.elements.zwemvaardigheid_voorkant"]();
    case "ZwemvaardigheidAchterkant":
      return m["rapporten.config.elements.zwemvaardigheid_achterkant"]();
    case "FietsvaardigheidVoorkant":
      return m["rapporten.config.elements.fietsvaardigheid_voorkant"]();
    case "FietsvaardigheidAchterkant":
      return m["rapporten.config.elements.fietsvaardigheid_achterkant"]();
    default: {
      const exhaustiveCheck: never = key;
      return exhaustiveCheck;
    }
  }
}
